import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  fetchArticles,
  fetchArticleById,
  fetchArticleCategories,
  deleteArticleApi,
  createArticleApi,
  updateArticleApi,
  validateArticleApi,
  deleteParagraphApi,
} from "../services/articlesService";

const ARTICLES_QUERY_KEY = ["articles"];
const ARTICLE_CATEGORIES_QUERY_KEY = ["articleCategories"];
const ARTICLE_QUERY_STALE_TIME = 60 * 1000;

export const useArticles = (params = {}) =>
  useQuery({
    queryKey: [...ARTICLES_QUERY_KEY, params],
    queryFn: () => fetchArticles(params),
    staleTime: ARTICLE_QUERY_STALE_TIME,
    refetchOnWindowFocus: false,
  });

export const useArticle = (articleId, options = {}) =>
  useQuery({
    queryKey: [...ARTICLES_QUERY_KEY, articleId],
    queryFn: () => fetchArticleById(articleId),
    enabled: !!articleId,
    staleTime: ARTICLE_QUERY_STALE_TIME,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useArticleCategories = () =>
  useQuery({
    queryKey: ARTICLE_CATEGORIES_QUERY_KEY,
    queryFn: fetchArticleCategories,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

export const useDeleteArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteArticleApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ARTICLES_QUERY_KEY });
      toast.success("Article supprimé avec succès", { autoClose: 3000 });
    },
    onError: () => {
      toast.error(
        "Une erreur est survenue lors de la suppression de l'article",
        { autoClose: 3000 }
      );
    },
  });
};

export const useCreateArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createArticleApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ARTICLES_QUERY_KEY });
      toast.success("Article bien ajouté", { autoClose: 3000 });
    },
    onError: (error) => {
      const message =
        error?.data?.message ||
        error?.message ||
        "Article non ajouté. Vérifiez les images et réessayez.";
      toast.error(message, { autoClose: 4000 });
    },
  });
};

export const useUpdateArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleId, payload }) => updateArticleApi(articleId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ARTICLES_QUERY_KEY });
      if (variables?.articleId) {
        queryClient.invalidateQueries({
          queryKey: [...ARTICLES_QUERY_KEY, variables.articleId],
        });
      }
      toast.success("Article bien modifié", { autoClose: 3000 });
    },
    onError: () => {
      toast.error("Article non modifié", { autoClose: 3000 });
    },
  });
};

export const useDeleteParagraph = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ paragraphId }) => deleteParagraphApi(paragraphId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ARTICLES_QUERY_KEY });
      if (variables?.articleId) {
        queryClient.invalidateQueries({
          queryKey: [...ARTICLES_QUERY_KEY, variables.articleId],
        });
      }
      toast.success("Paragraphe supprimé avec succès", { autoClose: 3000 });
    },
    onError: () => {
      toast.error("Une erreur est survenue lors de la suppression du paragraphe", {
        autoClose: 3000,
      });
    },
  });
};

export const useValidateArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (articleId) => validateArticleApi(articleId),
    onSuccess: (_data, articleId) => {
      queryClient.invalidateQueries({ queryKey: ARTICLES_QUERY_KEY });
      if (articleId) {
        queryClient.invalidateQueries({
          queryKey: [...ARTICLES_QUERY_KEY, articleId],
        });
      }
      toast.success("Article validé avec succes", { autoClose: 3000 });
    },
    onError: () => {
      toast.error("Article non validé ", { autoClose: 3000 });
    },
  });
};
